import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Flex, Space, Typography } from 'antd';
import {
    TbCopy,
    TbExternalLink,
    TbFileText,
    TbHighlight,
    TbShieldLock,
    TbTag,
    TbTextRecognition,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AttachmentDestination, CloudAttachment, CloudStorageConnection } from '@/services';
import ContextualCommentButton from '@/shared/components/activity/ContextualCommentButton';
import { PlatformAlert } from '@/shared/components/alerts';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import DocumentTable from '@/shared/components/tables/DocumentTable';
import { toast } from '@/shared/components/toasts/PlatformToast';

import type { AttachmentDocumentReview } from '../AttachmentPreview';
import DocumentConceptTag from '../AttachmentRedactionEditor/DocumentConceptTag';
import type { DocumentConceptReference } from '../AttachmentRedactionEditor/documentIntelligence';
import { resourceTypeForAttachmentDestination } from '../attachmentResource';
import type { ExtractedDocumentBlock, ExtractedTextSelection } from '../documentExtraction';
import { buildDocumentConceptAnnotations, type DocumentConceptTextAnnotation } from './documentConceptAnnotations';
import { createOntologySelection, OntologySelection } from './ontologySelection';
import useOntologyDocument from './useOntologyDocument';

interface OntologyDocumentReaderProps {
    attachment: CloudAttachment;
    canComment?: boolean;
    connection: CloudStorageConnection;
    destination?: AttachmentDestination;
    onReviewChange?: (review?: AttachmentDocumentReview) => void;
    onSecureRedact?: (selection: ExtractedTextSelection) => void;
    onViewSource?: (selection: ExtractedTextSelection) => void;
}

interface OntologyAnnotation {
    end: number;
    id: string;
    label?: string;
    start: number;
    type: 'classification' | 'highlight';
}

const renderManualAnnotationRange = (
    block: ExtractedDocumentBlock,
    annotations: OntologyAnnotation[],
    colors: { classification: string; highlight: string },
    start: number,
    end: number,
): ReactNode => {
    const relevant = annotations
        .filter((annotation) => annotation.end > start && annotation.start < end)
        .sort((left, right) => left.start - right.start);

    if (!relevant.length) {
        return block.text.slice(start, end);
    }

    const boundaries = Array.from(
        new Set([
            start,
            end,
            ...relevant.flatMap((annotation) => [Math.max(annotation.start, start), Math.min(annotation.end, end)]),
        ]),
    ).sort((left, right) => left - right);

    return boundaries.slice(0, -1).map((start, index) => {
        const end = boundaries[index + 1];
        const annotation = relevant.find((candidate) => candidate.start < end && candidate.end > start);
        const text = block.text.slice(start, end);

        return annotation ? (
            <mark
                key={`${block.id}:${start}`}
                title={annotation.label}
                style={{
                    background: annotation.type === 'classification' ? colors.classification : colors.highlight,
                    borderRadius: '0.125rem',
                    color: 'inherit',
                    padding: 0,
                }}
            >
                {text}
            </mark>
        ) : (
            <span key={`${block.id}:${start}`}>{text}</span>
        );
    });
};

const renderAnnotatedText = (
    block: ExtractedDocumentBlock,
    annotations: OntologyAnnotation[],
    conceptAnnotations: readonly DocumentConceptTextAnnotation[],
    colors: { classification: string; highlight: string },
    activeReferenceId: string | undefined,
    onReferenceSelect: (reference: DocumentConceptReference) => void,
    range: { end: number; start: number } = { end: block.text.length, start: 0 },
): ReactNode => {
    const relevantConcepts = conceptAnnotations.filter(
        (annotation) => annotation.end > range.start && annotation.start < range.end,
    );

    if (!relevantConcepts.length) {
        return renderManualAnnotationRange(block, annotations, colors, range.start, range.end);
    }

    const content: ReactNode[] = [];
    let cursor = range.start;

    relevantConcepts.forEach((annotation) => {
        const start = Math.max(annotation.start, range.start);
        const end = Math.min(annotation.end, range.end);

        if (start > cursor) {
            content.push(renderManualAnnotationRange(block, annotations, colors, cursor, start));
        }
        content.push(
            <DocumentConceptTag
                key={`${block.id}:${annotation.reference.id}:${start}`}
                active={activeReferenceId === annotation.reference.id}
                concept={annotation.concept}
                domain={annotation.domain}
                reference={annotation.reference}
                text={block.text.slice(start, end)}
                onSelect={onReferenceSelect}
            />,
        );
        cursor = end;
    });

    if (cursor < range.end) {
        content.push(renderManualAnnotationRange(block, annotations, colors, cursor, range.end));
    }

    return content;
};

const SemanticBlock: FC<{
    activeReferenceId?: string;
    annotations: OntologyAnnotation[];
    block: ExtractedDocumentBlock;
    colors: { classification: string; highlight: string };
    conceptAnnotations: readonly DocumentConceptTextAnnotation[];
    focused?: boolean;
    onReferenceSelect: (reference: DocumentConceptReference) => void;
}> = ({ activeReferenceId, annotations, block, colors, conceptAnnotations, focused, onReferenceSelect }) => {
    const { token } = useAntDesignTheme();
    const content = renderAnnotatedText(
        block,
        annotations,
        conceptAnnotations,
        colors,
        activeReferenceId,
        onReferenceSelect,
    );
    const commonStyle = {
        background: focused ? token.colorPrimaryBg : undefined,
        borderRadius: token.borderRadius,
        scrollMarginBlock: '5rem',
        transition: `background-color ${token.motionDurationMid}`,
    };
    const data = { 'data-ontology-block-id': block.id, 'data-ontology-page': block.page };

    if (block.kind === 'heading') {
        const fontSizes = [
            token.fontSizeHeading1,
            token.fontSizeHeading2,
            token.fontSizeHeading3,
            token.fontSizeHeading4,
        ];

        return (
            <Typography.Title
                {...data}
                level={Math.min(block.level ?? 3, 5) as 1 | 2 | 3 | 4 | 5}
                style={{
                    ...commonStyle,
                    fontSize: fontSizes[Math.min((block.level ?? 3) - 1, 3)],
                    margin: '1.25rem 0 0.5rem',
                }}
            >
                {content}
            </Typography.Title>
        );
    }

    if (block.kind === 'table' && block.tableRows?.length) {
        return (
            <DocumentTable
                blockId={block.id}
                focused={focused}
                page={block.page}
                renderCellText={(_text, range) =>
                    renderAnnotatedText(
                        block,
                        annotations,
                        conceptAnnotations,
                        colors,
                        activeReferenceId,
                        onReferenceSelect,
                        range,
                    )
                }
                rows={block.tableRows}
            />
        );
    }

    if (block.kind === 'list-item') {
        return (
            <div
                {...data}
                style={{
                    ...commonStyle,
                    display: 'flex',
                    gap: '0.625rem',
                    margin: '0.375rem 0',
                    paddingLeft: '0.5rem',
                }}
            >
                <span aria-hidden='true' style={{ color: token.colorTextTertiary }}>
                    {block.ordered ? '1.' : '•'}
                </span>
                <span>{content}</span>
            </div>
        );
    }

    if (block.kind === 'quote') {
        return (
            <blockquote
                {...data}
                style={{
                    ...commonStyle,
                    borderLeft: `0.1875rem solid ${token.colorPrimaryBorder}`,
                    color: token.colorTextSecondary,
                    margin: '0.75rem 0',
                    padding: '0.5rem 1rem',
                }}
            >
                {content}
            </blockquote>
        );
    }

    return (
        <Typography.Paragraph {...data} style={{ ...commonStyle, lineHeight: 1.75, marginBottom: '0.75rem' }}>
            {content}
        </Typography.Paragraph>
    );
};

const OntologyDocumentReader: FC<OntologyDocumentReaderProps> = ({
    attachment,
    canComment,
    connection,
    destination,
    onReviewChange,
    onSecureRedact,
    onViewSource,
}) => {
    const { token } = useAntDesignTheme();
    const rootRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const docxContainerRef = useRef<HTMLDivElement>(null);
    const { activeReferenceId, document, error, intelligence, loading, selectReference } = useOntologyDocument({
        attachment,
        connection,
        destination,
        docxContainerRef,
        onReviewChange,
    });
    const [annotations, setAnnotations] = useState<Record<string, OntologyAnnotation[]>>({});
    const [selection, setSelection] = useState<OntologySelection>();
    const conceptAnnotations = useMemo(
        () =>
            document
                ? buildDocumentConceptAnnotations(document, intelligence)
                : new Map<string, readonly DocumentConceptTextAnnotation[]>(),
        [document, intelligence],
    );
    const activeReference = useMemo(
        () =>
            intelligence.concepts
                .flatMap((concept) => concept.references)
                .find((reference) => reference.id === activeReferenceId),
        [activeReferenceId, intelligence.concepts],
    );
    const focusedBlockId = useMemo(() => {
        const annotatedBlock = [...conceptAnnotations.entries()].find(([, blockAnnotations]) =>
            blockAnnotations.some((annotation) => annotation.reference.id === activeReferenceId),
        )?.[0];
        const normalizedReference = activeReference?.text.replace(/\s+/g, ' ').trim().toLocaleLowerCase();

        return (
            annotatedBlock ??
            (normalizedReference
                ? document?.blocks.find(
                      (block) =>
                          block.page === activeReference?.page &&
                          block.text.toLocaleLowerCase().includes(normalizedReference),
                  )?.id
                : undefined)
        );
    }, [activeReference, activeReferenceId, conceptAnnotations, document?.blocks]);

    useEffect(() => {
        const clearWhenClickingAway = (event: PointerEvent) => {
            const target = event.target;

            if (target instanceof Node && (rootRef.current?.contains(target) || toolbarRef.current?.contains(target))) {
                return;
            }
            setSelection(undefined);
        };

        globalThis.document.addEventListener('pointerdown', clearWhenClickingAway);
        return () => globalThis.document.removeEventListener('pointerdown', clearWhenClickingAway);
    }, []);

    const updateSelection = () => {
        const root = rootRef.current;

        setSelection(root && document ? createOntologySelection(root, document) : undefined);
    };

    const clearSelection = () => {
        window.getSelection()?.removeAllRanges();
        setSelection(undefined);
    };

    const addAnnotation = (type: OntologyAnnotation['type'], label?: string) => {
        if (!selection) {
            return;
        }

        setAnnotations((current) => {
            const next = { ...current };

            selection.blockRanges.forEach((range) => {
                next[range.blockId] = [
                    ...(next[range.blockId] ?? []),
                    { ...range, id: `${type}:${Date.now()}:${range.blockId}`, label, type },
                ];
            });
            return next;
        });
        clearSelection();
    };

    const commentTarget = {
        component: `attachment:${attachment.id}`,
        label: `${attachment.name} semantic selection`,
        metadata: {
            breadcrumbs: ['Attachments', attachment.name, 'Ontology Mode'],
            selection: selection
                ? {
                      contextAfter: selection.contextAfter,
                      contextBefore: selection.contextBefore,
                      end: selection.end,
                      kind: 'ontology-text',
                      quote: selection.quote,
                      sourceRectangles: selection.sourceRectangles,
                      sourceReferences: selection.sourceReferences,
                      start: selection.start,
                  }
                : undefined,
            source: 'attachment-ontology-reader',
        },
        type: 'Document text',
    };
    const classificationItems = intelligence.concepts.slice(0, 30).map((concept) => ({
        icon: <TbTag size='1rem' />,
        key: concept.id,
        label: concept.label,
        onClick: () => addAnnotation('classification', concept.label),
    }));

    if (loading) {
        return (
            <>
                <div
                    ref={docxContainerRef}
                    aria-hidden='true'
                    style={{ left: '-10000px', position: 'fixed', visibility: 'hidden', width: '51rem' }}
                />
                <Flex
                    vertical
                    gap='1rem'
                    style={{ margin: '0 auto', maxWidth: '54rem', padding: '2rem', width: '100%' }}
                >
                    <BaseSkeleton active paragraph={{ rows: 2, width: ['45%', '70%'] }} title={false} />
                    <BaseSkeleton active paragraph={{ rows: 12 }} title={false} />
                </Flex>
            </>
        );
    }

    if (error || !document) {
        return (
            <EmptyView
                description={error ?? 'No semantic document is available for this attachment.'}
                height='100%'
                icon={<TbTextRecognition />}
                label='Ontology Mode Unavailable'
            />
        );
    }

    if (document.status === 'ocr-required') {
        return (
            <EmptyView
                description='This PDF has no embedded text. Run OCR before using semantic annotations or source-aware redaction.'
                height='100%'
                icon={<TbTextRecognition />}
                label='OCR Required'
            />
        );
    }

    if (document.status !== 'ready') {
        return (
            <EmptyView
                description={document.warnings[0] ?? 'No readable text was found in this attachment.'}
                height='100%'
                icon={<TbFileText />}
                label='No Extracted Text'
            />
        );
    }

    return (
        <div style={{ height: '100%', minHeight: 0, overflow: 'auto' }}>
            <div
                ref={docxContainerRef}
                aria-hidden='true'
                style={{ left: '-10000px', position: 'fixed', visibility: 'hidden', width: '51rem' }}
            />
            <article
                ref={rootRef}
                id={`ontology-document:${attachment.id}`}
                aria-label={`Semantic text for ${attachment.name}`}
                onKeyUp={updateSelection}
                onMouseUp={updateSelection}
                style={{ margin: '0 auto', maxWidth: '54rem', padding: '2rem', userSelect: 'text' }}
            >
                <Flex align='center' gap='0.5rem' style={{ color: token.colorTextTertiary, marginBottom: '1.5rem' }}>
                    <TbFileText />
                    <small>
                        {document.pageCount} page{document.pageCount === 1 ? '' : 's'}
                    </small>
                    <small>·</small>
                    <small>{document.wordCount.toLocaleString()} words</small>
                </Flex>
                {document.blocks.map((block) => (
                    <SemanticBlock
                        key={block.id}
                        activeReferenceId={activeReferenceId}
                        annotations={annotations[block.id] ?? []}
                        block={block}
                        colors={{ classification: token.colorWarningBg, highlight: token.colorPrimaryBg }}
                        conceptAnnotations={conceptAnnotations.get(block.id) ?? []}
                        focused={focusedBlockId === block.id}
                        onReferenceSelect={selectReference}
                    />
                ))}
                <PlatformAlert
                    description='Semantic annotations never alter the original file. Only source-aware PDF selections can create a flattened, secure redacted derivative.'
                    showIcon
                    size='small'
                    status='info'
                    style={{ marginTop: '2rem' }}
                    title='Original document preserved'
                />
            </article>
            {selection ? (
                <div
                    ref={toolbarRef}
                    role='toolbar'
                    aria-label='Semantic text actions'
                    onMouseDown={(event) => event.preventDefault()}
                    style={{
                        background: token.colorBgElevated,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        boxShadow: token.boxShadowSecondary,
                        left: selection.toolbar.left,
                        padding: '0.25rem',
                        position: 'fixed',
                        top: selection.toolbar.top,
                        zIndex: token.zIndexPopupBase,
                    }}
                >
                    <Space.Compact size='small'>
                        {destination ? (
                            <ContextualCommentButton
                                buttonLabel='Comment'
                                buttonType='default'
                                disabled={canComment === false}
                                resource={{
                                    resourceId: destination.resourceId,
                                    resourceTitle: destination.resourceName,
                                    resourceType: resourceTypeForAttachmentDestination(destination),
                                }}
                                target={commentTarget}
                            />
                        ) : null}
                        <Button
                            icon={<TbCopy />}
                            onClick={() => {
                                void navigator.clipboard
                                    .writeText(selection.quote)
                                    .then(() => toast.success('Selected text copied'))
                                    .catch(() => toast.error('Selected text could not be copied'));
                                clearSelection();
                            }}
                        >
                            Copy
                        </Button>
                        <Button icon={<TbHighlight />} onClick={() => addAnnotation('highlight')}>
                            Highlight
                        </Button>
                        <Dropdown
                            disabled={!classificationItems.length}
                            placement='bottomLeft'
                            trigger={['click']}
                            popupRender={() => (
                                <DropdownMenuPanel items={classificationItems} style={{ minWidth: '15rem' }} />
                            )}
                        >
                            <Button icon={<TbTag />}>Classify</Button>
                        </Dropdown>
                        <Button
                            disabled={!onViewSource}
                            icon={<TbExternalLink />}
                            onClick={() => {
                                onViewSource?.(selection);
                                clearSelection();
                            }}
                        >
                            View in Source
                        </Button>
                        <Button
                            disabled={!selection.sourceRectangles.length || !onSecureRedact}
                            icon={<TbShieldLock />}
                            title={
                                selection.sourceRectangles.length
                                    ? undefined
                                    : 'Secure redaction requires a source-aware PDF selection'
                            }
                            onClick={() => {
                                onSecureRedact?.(selection);
                                clearSelection();
                            }}
                        >
                            Redact
                        </Button>
                    </Space.Compact>
                </div>
            ) : null}
        </div>
    );
};

export default OntologyDocumentReader;
